import { useCallback, useMemo, useRef, useState } from 'react'
import { NextStepFormsState } from '../contexts/NextStepFormsContext'

// One empty form to begin with: opening a tension in a meeting is opening the
// question "what is the next step?" (G2-D24). Whether it is shown at all is
// decided where it is rendered — outside a meeting, nothing displays it.
//
// `threadId` is not read, only watched: the forms belong to ONE tension, and
// the state must start over when the host component is reused for another one
// (revue du bloc, 11/08/2026). Today the modal unmounts between two tensions,
// so nothing was observed — but nothing guaranteed it either, and the failure
// would have been a step typed for A created on B.
export default function useNextStepForms(threadId: string): NextStepFormsState {
  const [forms, setForms] = useState<number[]>([0])
  const nextKey = useRef(1)

  // Reset DURING render, not in an effect: an effect runs after the paint, so
  // tension B would show tension A's forms for one frame — long enough to
  // submit into. This is React's documented way to adjust state on a prop
  // change. The fresh key matters as much as the reset: reusing key 0 would
  // let React keep the same form instance, and the text typed with it.
  const [renderedThreadId, setRenderedThreadId] = useState(threadId)
  if (threadId !== renderedThreadId) {
    setRenderedThreadId(threadId)
    setForms([nextKey.current++])
  }

  const addForm = useCallback(
    () => setForms((f) => [...f, nextKey.current++]),
    []
  )
  const removeForm = useCallback(
    (key: number) => setForms((f) => f.filter((k) => k !== key)),
    []
  )

  return useMemo(
    () => ({ forms, addForm, removeForm }),
    [forms, addForm, removeForm]
  )
}
